import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import JobsStore from '../../redux/jobs';
import { useAddToast } from '../../contexts/ToastContext';
import { useInterval } from '../../hooks';
import { get } from '../../redux/api';
import { failureMessages, successMessages } from './consts';
import type { GetJobsResponse } from '../../../shared/types/routes/jobs';

/**
 * This is a component that handles displaying Toast notifications when backend Faktory jobs succeed or fail.
 * Using redux, it monitors a set of Jobs and polls the API for updates about their status. When it sees a job that
 * has succeeded or failed, it triggers a Toast for the job, and removes it from the set of Jobs being polled for.
 */
const NotificationHub: VoidFunctionComponent = () => {
  const addToast = useAddToast();

  const dispatch = useDispatch();
  const jobs = useSelector(JobsStore.selectors.getJobs);
  const jobTokens = useSelector(JobsStore.selectors.getJobTokens);

  const [hasFetchErrored, setHasFetchErrored] = useState(false);

  useInterval(async () => {
    if (!_.isEmpty(jobs) && !hasFetchErrored) {
      try {
        // Data will be a record where the keys are jobTokens and the values contain the job status.
        const { data } = await get<GetJobsResponse>('/api/jobs/status', false, { jobs: jobTokens });

        // Build an array of job tokens to remove based on their success status.
        let jobsToRemove = Object.entries(data?.jobs ?? {}).reduce<string[]>((acc, [key, value]) => {
          const existingJob = jobs[key];

          if (value.status === 'finished') {
            const successMessage = existingJob ? successMessages[existingJob.name] : undefined;
            addToast({
              type: 'success',
              content: successMessage ?? `Job ${key} finished.`,
            });
            acc.push(key);
          } else if (value.status === 'failed') {
            const failureMessage = existingJob ? failureMessages[existingJob.name] : undefined;
            addToast({
              type: 'error',
              content: failureMessage ?? `Job ${key} failed.`,
            });
            acc.push(key);
          }

          return acc;
        }, []);

        // If any jobTokens are missing from the response, remove those from the store as well.
        jobsToRemove = [...jobsToRemove, ..._.difference(jobTokens, Object.keys(data?.jobs ?? {}))];

        if (!_.isEmpty(jobsToRemove)) {
          dispatch(JobsStore.actions.removeJob(jobsToRemove));
        }
      } catch (e) {
        addToast({
          type: 'error',
          content: 'Something went wrong while fetching job statuses.',
        });

        setHasFetchErrored(true);
      }
    }
  }, 5000);

  return (null);
};

export default NotificationHub;
